import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  FormHelperText,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Avatar,
  Paper,
  Divider,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Quiz as QuizIcon,
  Add as AddIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import BreadcrumbsNav from '../common/BreadcrumbsNav';
import ModernButton from '../common/ModernButton';
import ModernTextField from '../common/ModernTextField';
import { useToast } from '../../contexts/ToastContext';
import useAsync from '../../hooks/useAsync';
import { QuestionTypes } from '../../constants/questionTypes';

// Types that hide question text field and auto-mark answer as correct
const hideQuestionTextTypes = [
  QuestionTypes.NOTE_COMPLETION_ONE_WORD,
  QuestionTypes.SUMMARY_COMPLETION_TWO_WORDS,
  QuestionTypes.SENTENCE_COMPLETION_ONE_WORD,
  QuestionTypes.MULTIPLE_CHOICE_MULTIPLE_ANSWER,
];

// Matching types use questionText field
const matchingTypes = [
  QuestionTypes.MATCHING_FEATURES,
  QuestionTypes.MATCHING_HEADINGS,
  QuestionTypes.MATCHING_INFORMATION,
  QuestionTypes.MATCHING_ENDINGS,
];

const fixedTrueFalseOptions = ['TRUE', 'FALSE', 'NOT GIVEN'];
const fixedYesNoOptions = ['YES', 'NO', 'NOT GIVEN'];
const defaultMultipleChoiceOptionsReading = [
  { answer: '', isCorrect: false },
  { answer: '', isCorrect: false },
  { answer: '', isCorrect: false },
  { answer: '', isCorrect: false },
];
const defaultMultipleChoiceOptionsListening = [
  { answer: '', isCorrect: false },
  { answer: '', isCorrect: false },
  { answer: '', isCorrect: false },
];

const initAnswers = (type, isListening = false) => {
  if (type === QuestionTypes.TRUE_FALSE) {
    return fixedTrueFalseOptions.map((option) => ({ answer: option, isCorrect: false }));
  }
  if (type === QuestionTypes.YES_NO) {
    return fixedYesNoOptions.map((option) => ({ answer: option, isCorrect: false }));
  }
  if (type === QuestionTypes.MULTIPLE_CHOICE_SINGLE_ANSWER) {
    return isListening ? defaultMultipleChoiceOptionsListening : defaultMultipleChoiceOptionsReading;
  }
  if (type === QuestionTypes.MULTIPLE_CHOICE_MULTIPLE_ANSWER) {
    return [
      { key: 'A', answer: 'A', isCorrect: true },
      { key: 'B', answer: 'B', isCorrect: true },
    ];
  }
  if (hideQuestionTextTypes.includes(type)) {
    return [{ answer: '', isCorrect: true }];
  }
  if (matchingTypes.includes(type)) {
    return [{ key: '', answer: '', isCorrect: true }];
  }
  return [{ answer: '', isCorrect: false }];
};

/**
 * QuestionFormBase - Shared form component for Reading and Listening questions
 */
const QuestionFormBase = ({
  parentId,
  questionSetId,
  questionId,
  section,
  sectionKey,
  sectionLabel,
  initialType,
  questionCount,
  startQuestionNumber = 1,
  basePath,
  api,
  answerOptions = [],
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isEditMode = Boolean(questionId);

  const [formData, setFormData] = useState({
    questionNumber: '',
    question: '',
    questionText: '',
    answers: [],
  });
  const [currentIndex, setCurrentIndex] = useState(startQuestionNumber);
  const [loading, setLoading] = useState(isEditMode);
  const [errors, setErrors] = useState({});

  const { execute: saveQuestion, loading: saving } = useAsync(async (payload) => {
    if (isEditMode) {
      await api.updateQuestion(parentId, section, questionSetId, questionId, payload);
    } else {
      await api.createQuestion(parentId, section, questionSetId, payload);
    }
  });

  const questionType = isEditMode ? formData.type || initialType : initialType;
  const isSingleChoice = questionType !== QuestionTypes.MULTIPLE_CHOICE_MULTIPLE_ANSWER;
  const isListening = basePath?.includes('listening');

  // Initialize answers in create mode
  useEffect(() => {
    if (!isEditMode && initialType) {
      let answers = initAnswers(initialType, isListening);
      if (initialType === QuestionTypes.MULTIPLE_CHOICE_MULTIPLE_ANSWER && answerOptions.length > 0) {
        answers = answerOptions.map((opt) => ({ key: opt.key, answer: opt.answer, isCorrect: false }));
      }
      setFormData({
        questionNumber: currentIndex,
        question: '',
        questionText: '',
        answers,
      });
    }
  }, [isEditMode, initialType, currentIndex, isListening, answerOptions]);

  // Fetch question in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchQuestion = async () => {
        try {
          const response = await api.getQuestionById(parentId, section, questionSetId, questionId);
          const data = response.data;
          let updatedData = { ...data };

          if (data.questionText && !data.question) {
            updatedData.question = data.questionText;
          }
          if (matchingTypes.includes(initialType)) {
            updatedData.questionText = data.question || '';
          } else {
            updatedData.questionText = updatedData.question || '';
          }
          updatedData.type = data.type || initialType;

          // Handle different question types
          if (initialType === QuestionTypes.TRUE_FALSE) {
            updatedData.answers = fixedTrueFalseOptions.map((option) => ({
              answer: option,
              isCorrect: data.answers?.find((a) => a.answer === option)?.isCorrect || false,
            }));
          } else if (initialType === QuestionTypes.YES_NO) {
            updatedData.answers = fixedYesNoOptions.map((option) => ({
              answer: option,
              isCorrect: data.answers?.find((a) => a.answer === option)?.isCorrect || false,
            }));
          } else if (initialType === QuestionTypes.MULTIPLE_CHOICE_SINGLE_ANSWER) {
            const expectedLength = isListening ? 3 : 4;
            const defaultOptions = isListening ? defaultMultipleChoiceOptionsListening : defaultMultipleChoiceOptionsReading;
            updatedData.answers = data.answers?.length === expectedLength ? data.answers : defaultOptions;
          } else if (initialType === QuestionTypes.MULTIPLE_CHOICE_MULTIPLE_ANSWER) {
            if (answerOptions.length > 0) {
              const correctKeys = new Set(data.answers?.filter((a) => a.isCorrect).map((a) => a.key));
              updatedData.answers = answerOptions.map((opt) => ({
                key: opt.key,
                answer: opt.answer,
                isCorrect: correctKeys.has(opt.key),
              }));
            } else if (data.answers?.length > 0) {
              updatedData.answers = data.answers.map((ans, index) => ({
                ...ans,
                key: ans.key || String.fromCharCode(65 + index),
                answer: ans.answer ? ans.answer.toUpperCase() : ans.key || String.fromCharCode(65 + index),
              }));
            } else {
              updatedData.answers = [
                { key: 'A', answer: 'A', isCorrect: true },
                { key: 'B', answer: 'B', isCorrect: true },
              ];
            }
          } else if (hideQuestionTextTypes.includes(initialType)) {
            if (!data.answers || data.answers.length === 0) {
              updatedData.answers = [{ answer: '', isCorrect: true }];
            }
          } else if (matchingTypes.includes(initialType)) {
            updatedData.answers = data.answers?.length > 0
              ? data.answers.map((a) => ({ ...a, key: a.answer || a.key }))
              : [{ key: '', answer: '', isCorrect: true }];
          } else {
            updatedData.answers = data.answers?.length > 0 ? data.answers : [{ answer: '', isCorrect: false }];
          }

          setFormData(updatedData);
        } catch (error) {
          console.error('Failed to fetch question:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchQuestion();
    }
  }, [isEditMode, parentId, section, questionSetId, questionId, initialType, api]);

  const handleAnswerChange = (index, field, value) => {
    const updatedAnswers = [...formData.answers];

    if (questionType === QuestionTypes.MULTIPLE_CHOICE_MULTIPLE_ANSWER) {
      if (field === 'key' || field === 'answer') {
        const uppercaseValue = value.toUpperCase();
        updatedAnswers[index] = {
          ...updatedAnswers[index],
          key: uppercaseValue,
          answer: uppercaseValue,
        };
      } else {
        updatedAnswers[index] = { ...updatedAnswers[index], [field]: value };
      }
    } else {
      updatedAnswers[index] = { ...updatedAnswers[index], [field]: value };
    }

    setFormData((prev) => ({ ...prev, answers: updatedAnswers }));
    setErrors((prev) => ({ ...prev, correct: undefined }));
  };

  const toggleCorrect = (index) => {
    setFormData((prev) => {
      const updated = prev.answers.map((ans, i) => {
        if (i === index) {
          const newVal = !ans.isCorrect;

          if (questionType === QuestionTypes.MULTIPLE_CHOICE_MULTIPLE_ANSWER) {
            const currentCorrectCount = prev.answers.filter((a) => a.isCorrect).length;
            if (newVal && currentCorrectCount >= 2) return ans;
            if (!newVal && currentCorrectCount <= 2) return ans;
          }

          return { ...ans, isCorrect: newVal };
        }
        return isSingleChoice ? { ...ans, isCorrect: false } : ans;
      });
      return { ...prev, answers: updated };
    });
    setErrors((prev) => ({ ...prev, correct: undefined }));
  };

  const handleAddAnswer = () => {
    if (
      questionType !== QuestionTypes.TRUE_FALSE &&
      questionType !== QuestionTypes.MULTIPLE_CHOICE_SINGLE_ANSWER &&
      questionType !== QuestionTypes.YES_NO
    ) {
      if (questionType === QuestionTypes.MULTIPLE_CHOICE_MULTIPLE_ANSWER && formData.answers.length >= 2) {
        return;
      }

      const newAnswer =
        questionType === QuestionTypes.MULTIPLE_CHOICE_MULTIPLE_ANSWER
          ? {
              key: String.fromCharCode(65 + formData.answers.length),
              answer: String.fromCharCode(65 + formData.answers.length),
              isCorrect: false,
            }
          : { answer: '', isCorrect: false };

      setFormData((prev) => ({ ...prev, answers: [...prev.answers, newAnswer] }));
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'question' ? { questionText: value } : {}),
    }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.questionNumber) {
      newErrors.questionNumber = 'Question number is required';
    }
    if (!hideQuestionTextTypes.includes(questionType)) {
      const questionTextValue = matchingTypes.includes(questionType) ? formData.questionText : formData.question;
      if (!questionTextValue || !questionTextValue.trim()) {
        newErrors.question = 'Question text is required';
      }
    }
    if (matchingTypes.includes(questionType)) {
      if (!formData.answers[0]?.key) {
        newErrors.correct = 'Please select an answer option';
      }
    } else if (questionType === QuestionTypes.MULTIPLE_CHOICE_MULTIPLE_ANSWER) {
      const correctAnswers = formData.answers.filter((a) => a.isCorrect);
      if (correctAnswers.length !== 2) {
        newErrors.correct = 'Exactly 2 correct answers are required';
      }
    } else if (!formData.answers.some((a) => a.isCorrect)) {
      newErrors.correct = 'Select at least one correct answer';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      const payload = {
        ...formData,
        questionText: matchingTypes.includes(questionType) ? formData.questionText : formData.question,
        question: matchingTypes.includes(questionType) ? formData.questionText : formData.question,
      };

      await saveQuestion(payload);
      const lastQuestionNumber = startQuestionNumber + (questionCount || 1) - 1;

      if (isEditMode) {
        showToast('Question updated successfully!', 'success');
        navigate(-1);
      } else if (currentIndex < lastQuestionNumber) {
        showToast('Question saved', 'success');
        const nextIndex = currentIndex + 1;
        setCurrentIndex(nextIndex);
        let nextAnswers = initAnswers(initialType, isListening);
        if (initialType === QuestionTypes.MULTIPLE_CHOICE_MULTIPLE_ANSWER && answerOptions.length > 0) {
          nextAnswers = answerOptions.map((opt) => ({ key: opt.key, answer: opt.answer, isCorrect: false }));
        }
        setFormData({
          questionNumber: nextIndex,
          question: '',
          questionText: '',
          answers: nextAnswers,
        });
      } else {
        showToast('Question created successfully!', 'success');
        navigate(`${basePath}/${parentId}/question-sets/${questionSetId}/questions`, {
          state: { [sectionKey]: section, type: initialType },
        });
      }
    } catch (error) {
      console.error('Error submitting question:', error);
      showToast('An error occurred. Please try again.', 'error');
    }
  };

  if (loading) return <Typography>Loading question data...</Typography>;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <BreadcrumbsNav
          current={isEditMode ? 'Edit Question' : 'Create Question'}
          links={[
            {
              label: 'Question Set List',
              onClick: () => navigate(`${basePath}/${parentId}/question-sets`, { state: { [sectionKey]: section, type: questionType } }),
            },
            {
              label: 'Question List',
              onClick: () =>
                navigate(`${basePath}/${parentId}/question-sets/${questionSetId}/questions`, { state: { [sectionKey]: section, type: questionType } }),
            },
          ]}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ backgroundColor: 'primary.main', width: 48, height: 48, mr: 2 }}>
            <QuizIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {isEditMode ? 'Edit Question' : 'Create New Question'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {isEditMode ? `Update question for ${section}` : `Add a new question to ${section} - Question ${currentIndex}`}
            </Typography>
          </Box>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Main Form */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ backgroundColor: 'info.main', width: 32, height: 32, mr: 2 }}>
                    <InfoIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Question Details
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="Question Number"
                      type="number"
                      value={formData.questionNumber}
                      onChange={(e) => handleFieldChange('questionNumber', e.target.value)}
                      error={Boolean(errors.questionNumber)}
                      helperText={errors.questionNumber}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                        Question Type
                      </Typography>
                      <Chip label={questionType} color="primary" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 500 }} />
                    </Box>
                  </Grid>
                </Grid>

                {!hideQuestionTextTypes.includes(questionType) && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                      Question Text
                    </Typography>
                    <ModernTextField
                      label="Enter the question text"
                      value={matchingTypes.includes(questionType) ? formData.questionText : formData.question}
                      onChange={(e) => {
                        const fieldName = matchingTypes.includes(questionType) ? 'questionText' : 'question';
                        handleFieldChange(fieldName, e.target.value);
                      }}
                      multiline
                      rows={3}
                      error={Boolean(errors.question)}
                      helperText={errors.question}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Answers Card */}
            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ backgroundColor: 'success.main', width: 32, height: 32, mr: 2 }}>
                    <CheckCircleIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Answer Options
                  </Typography>
                </Box>

                {(formData.answers ?? []).map((answer, index) => (
                  <Paper
                    key={index}
                    sx={{
                      p: 3,
                      mb: 2,
                      border: '1px solid',
                      borderColor: answer.isCorrect ? 'success.main' : 'grey.300',
                      backgroundColor: answer.isCorrect ? 'success.50' : 'background.paper',
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                          Answer {index + 1}
                        </Typography>

                        {matchingTypes.includes(questionType) ? (
                          <FormControl fullWidth>
                            <InputLabel>Select Answer</InputLabel>
                            <Select
                              value={answer.key || ''}
                              label="Select Answer"
                              onChange={(e) => {
                                const selected = answerOptions.find((opt) => opt.key === e.target.value);
                                if (selected) {
                                  const updatedAnswers = [...formData.answers];
                                  updatedAnswers[index] = { key: selected.key, answer: selected.answer, isCorrect: true };
                                  setFormData((prev) => ({ ...prev, answers: updatedAnswers }));
                                  setErrors((prev) => ({ ...prev, correct: undefined }));
                                }
                              }}
                            >
                              {answerOptions.map((opt) => (
                                <MenuItem key={opt.key} value={opt.key}>
                                  {opt.key}: {opt.answer}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : questionType === QuestionTypes.MULTIPLE_CHOICE_MULTIPLE_ANSWER ? (
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <ModernTextField
                              label="Key"
                              value={answer.key || ''}
                              onChange={(e) => handleAnswerChange(index, 'key', e.target.value)}
                              disabled
                              sx={{ width: '80px' }}
                            />
                            <ModernTextField
                              label="Answer"
                              value={answer.answer || ''}
                              onChange={(e) => handleAnswerChange(index, 'answer', e.target.value)}
                              fullWidth
                              disabled={answerOptions.length > 0}
                            />
                          </Box>
                        ) : (
                          <ModernTextField
                            label={`Answer ${index + 1}`}
                            value={answer.answer}
                            onChange={(e) => handleAnswerChange(index, 'answer', e.target.value)}
                            fullWidth
                            disabled={questionType === QuestionTypes.TRUE_FALSE || questionType === QuestionTypes.YES_NO}
                          />
                        )}
                      </Box>

                      {!matchingTypes.includes(questionType) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Tooltip title={answer.isCorrect ? 'Correct answer' : 'Mark as correct'} arrow>
                            <IconButton
                              onClick={() => toggleCorrect(index)}
                              sx={{
                                color: answer.isCorrect ? 'success.main' : 'grey.400',
                                '&:hover': { backgroundColor: answer.isCorrect ? 'success.100' : 'grey.100' },
                              }}
                            >
                              {answer.isCorrect ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                            </IconButton>
                          </Tooltip>
                          <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                            {answer.isCorrect ? 'Correct' : 'Mark correct'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                ))}

                {errors.correct && <FormHelperText error sx={{ mb: 2, mt: 1 }}>{errors.correct}</FormHelperText>}

                {questionType !== QuestionTypes.TRUE_FALSE &&
                  questionType !== QuestionTypes.MULTIPLE_CHOICE_SINGLE_ANSWER &&
                  questionType !== QuestionTypes.YES_NO &&
                  !matchingTypes.includes(questionType) &&
                  !(questionType === QuestionTypes.MULTIPLE_CHOICE_MULTIPLE_ANSWER && answerOptions.length > 0) && (
                    <Box sx={{ mt: 2 }}>
                      <ModernButton onClick={handleAddAnswer} variant="outlined" startIcon={<AddIcon />} sx={{ borderRadius: 2 }}>
                        Add Another Answer
                      </ModernButton>
                    </Box>
                  )}
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ borderRadius: 3, position: 'sticky', top: 100 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                  Question Preview
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                    {sectionLabel}
                  </Typography>
                  <Chip label={section} color="primary" variant="outlined" sx={{ textTransform: 'capitalize', fontWeight: 500 }} />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                    Question Number
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    {formData.questionNumber || 'Not set'}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                    Correct Answers
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                    {(formData.answers ?? []).filter((a) => a.isCorrect).length} selected
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <ModernButton type="submit" variant="contained" startIcon={<SaveIcon />} loading={saving} fullWidth>
                    {isEditMode ? 'Save Changes' : 'Save Question'}
                  </ModernButton>
                  <ModernButton
                    onClick={() =>
                      navigate(`${basePath}/${parentId}/question-sets/${questionSetId}/questions`, { state: { [sectionKey]: section, type: questionType } })
                    }
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    fullWidth
                  >
                    Back to Questions
                  </ModernButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default QuestionFormBase;
